import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Link,
  Stack,
  Typography
} from '@mui/material'
import { useEffect, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'
import FundingLogos from '../../components/common/FundingLogos'
import {
  useSignUpConfirmMutation,
  useSignUpResendMutation
} from '../../hooks/useMutations/useAuthMutations'
import { useSnackbar } from '../../hooks/useSnackbar'

export default function SignUpConfirm() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email')
  const code = searchParams.get('code')
  const { showSnackbar } = useSnackbar()
  const hasConfirmed = useRef(false)

  const signUpConfirmMutation = useSignUpConfirmMutation()
  const signUpResendMutation = useSignUpResendMutation()

  useEffect(() => {
    if (email && code && !hasConfirmed.current) {
      hasConfirmed.current = true
      setTimeout(() => {
        signUpConfirmMutation.mutate(
          { email, code },
          {
            onSuccess: () => {
              navigate('/sign-in')
            },
            onError: (error: any) => {
              if (error?.response.status == 409) {
                showSnackbar({
                  message: t('confirm_sign_up.confirm.already_verified'),
                  severity: 'info'
                })
                navigate('/sign-in')
              } else {
                showSnackbar({
                  message: t('confirm_sign_up.confirm.error'),
                  severity: 'error'
                })
              }
            }
          }
        )
      }, 0)
    }
  }, [
    email,
    code,
    signUpConfirmMutation,
    navigate,
    t,
    signUpConfirmMutation.mutate,
    showSnackbar
  ])

  return (
    <>
      <Stack
        direction='column'
        spacing='1rem'
        sx={{
          width: '37.875rem',
          minHeight: '37.875rem',
          padding: '2.5rem',
          borderRadius: '1.5rem',
          backgroundColor: 'background.default',
          justifyContent: 'space-between'
        }}
      >
        <Stack direction='column' spacing='1.5rem'>
          <Typography variant='h4'>{t('confirm_sign_up.title')}</Typography>
          <Typography variant='body1'>
            <Trans
              i18nKey='confirm_sign_up.description'
              components={[
                <Typography
                  variant='body1'
                  fontWeight='bold'
                  display='inline'
                />
              ]}
            />
          </Typography>
          <Typography variant='body1'>
            <Trans
              i18nKey='confirm_sign_up.already_verified'
              components={[
                <Typography
                  variant='body1'
                  fontWeight='bold'
                  display='inline'
                />
              ]}
            />
          </Typography>

          <Button
            variant='outlined'
            sx={{ textTransform: 'none', fontWeight: 500 }}
            size='large'
            onClick={() => {
              showSnackbar({
                message: t('confirm_sign_up.sign_in_reminder'),
                severity: 'info',
                anchorOrigin: { vertical: 'top', horizontal: 'right' }
              })
              navigate('/sign-in')
            }}
          >
            {t('confirm_sign_up.sign_in')}
          </Button>
        </Stack>

        <Stack
          direction='column'
          spacing='0.5rem'
          sx={{ alignItems: 'flex-start' }}
        >
          <Typography variant='body1'>
            {t('confirm_sign_up.not_received')}
          </Typography>
          <Link
            variant='body1'
            component='button'
            onClick={() =>
              signUpResendMutation.mutate(
                { email: email || '' },
                {
                  onSuccess: () => {
                    showSnackbar({
                      message: t('confirm_sign_up.resend.success'),
                      severity: 'success'
                    })
                  },
                  onError: () => {
                    showSnackbar({
                      message: t('confirm_sign_up.resend.error'),
                      severity: 'error'
                    })
                  }
                }
              )
            }
            sx={{
              color: signUpResendMutation.isPending
                ? 'text.disabled'
                : 'primary.main',
              textAlign: 'left'
            }}
          >
            {t('confirm_sign_up.resend.link')}
          </Link>
        </Stack>
        <FundingLogos size='small' sx={{ mt: '1.5rem' }} />
      </Stack>
      <Dialog open={signUpConfirmMutation.isPending}>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '3rem'
          }}
        >
          <CircularProgress />
          <Typography>{t('confirm_sign_up.confirm.pending')}</Typography>
        </DialogContent>
      </Dialog>
    </>
  )
}
